<template>
	<auth-layout :prev="{ name: 'login' }">
		<div class="col-lg-8 align-self-center">
			<div class="reset-password">
				<b-alert v-model="success" variant="success">
					{{ successMessage }}
				</b-alert>

				<h1>{{ $t('auth.password_reset.title') }}</h1>

				<b-form @submit.prevent="reset" novalidate>
					<div class="form-group">
						<text-input
							name="email"
							:label="$t('auth.password_reset.email')"
							autocomplete="email"
							:autofocus="true"
							:error="emailError"
							v-model="email"
						/>
					</div>

					<div class="form-group">
						<text-input
							name="password"
							type="password"
							:label="$t('auth.password_reset.password')"
							autocomplete="new-password"
							:error="passwordError"
							v-model="password"
						/>
						<b-form-text>
							Password must be at least 8 characters and at least 3 of the following: uppercase letter, lowercase letter, digit, special character ( ! @ # $ _ )
						</b-form-text>
					</div>

					<div class="form-group">
						<text-input
							name="password_confirmation"
							type="password"
							:label="$t('auth.password_reset.password_confirm')"
							autocomplete="new-password"
							:error="passwordConfirmError"
							v-model="passwordConfirm"
						/>
					</div>

					<b-button
						type="submit"
						variant="primary"
						block
						:disabled="processing || success"
					>
						<b-spinner small v-if="processing" />
						{{ $t('auth.password_reset.submit_btn') }}
					</b-button>
				</b-form>
			</div>
		</div>
	</auth-layout>
</template>

<script>
import AuthLayout from '../layouts/AuthLayout';
import TextInput from '../../components/forms/TextInput';
import { required, email as vEmail, sameAs } from 'vuelidate/lib/validators';
import nprogress from 'nprogress';
import validate from '../../util/validate';
import ErrorModal from '../../mixins/ErrorModal';

export default {
	components: {
		AuthLayout,
		TextInput
	},

	inject: [ 'passwordRepository' ],

	mixins: [ ErrorModal ],

	/**
	 * If the token is empty, redirect the user to the forgot password screen.
	 *
	 * @returns {void}
	 */
	beforeMount () {
		if (this.token === '' && this.token === null && this.token === undefined) {
			this.$router.push({ name: 'forgot-password' });
		}
	},

	data () {
		return {
			email: this.$route.query.email,
			password: '',
			passwordConfirm: '',
			processing: false,
			submitted: false,
			serverError: null,
			success: false,
			successMessage: ''
		};
	},

	validations: {
		email: {
			required,
			vEmail
		},
		password: {
			required
		},
		passwordConfirm: {
			sameAsPassword: sameAs('password')
		}
	},

	computed: {
		/**
		 * Returns the token from the route query.
		 *
		 * @returns {string}
		 */
		token () {
			return this.$route.query.token;
		},

		genericServerError () {
			return validate.getGenericError(this.serverError);
		},

		/**
		 * Returns the error for the email field after submission, if any.
		 *
		 * @returns {string}
		 */
		emailError () {
			if (this.submitted) {
				if (!this.$v.email.required) {
					return this.$t('validation.required');
				}
				if (!this.$v.email.vEmail) {
					return this.$t('validation.email');
				}
				if (validate.getServerError(this.serverError, 'email') !== '') {
					return validate.getServerError(this.serverError, 'email');
				}
			}

			return '';
		},

		/**
		 * Returns the error for the password field after submission, if any.
		 *
		 * @returns {string}
		 */
		passwordError () {
			if (this.submitted) {
				if (!this.$v.password.required) {
					return this.$t('validation.required');
				}
				if (validate.getServerError(this.serverError, 'password') !== '') {
					return validate.getServerError(this.serverError, 'password');
				}
			}

			return '';
		},

		/**
		 * Returns the error for the password confirmation field after submission, if any.
		 *
		 * @returns {string}
		 */
		passwordConfirmError () {
			if (this.submitted) {
				if (!this.$v.passwordConfirm.sameAsPassword) {
					return this.$t('validation.password_confirm');
				}
				if (validate.getServerError(this.serverError, 'password_confirmation') !== '') {
					return validate.getServerError(this.serverError, 'password_confirmation');
				}
			}

			return '';
		}
	},

	watch: {
		genericServerError () {
			if (this.genericServerError !== null) {
				this.showErrorModal(this.genericServerError, 'Error Resetting Password').then(() => {
					this.serverError = null;
				});
			}
		}
	},

	methods: {
		/**
		 * Sends a password reset request to the API.
		 *
		 * @returns {void}
		 */
		async reset () {
			this.processing = true;
			this.submitted = true;
			this.success = false;
			this.successMessage = '';

			if (this.$v.$invalid) {
				this.processing = false;
			}
			else {
				nprogress.start();

				try {
					this.successMessage = await this.passwordRepository.resetPassword(
						this.email, this.token, this.password, this.passwordConfirm
					);
					this.success = true;
					this.submitted = false;
					this.password = '';
					this.passwordConfirm = '';
				}
				catch (e) {
					this.success = false;
					this.serverError = e;
				}

				this.processing = false;
				nprogress.done();
			}
		}
	}
};
</script>
